<template>
  <div class="bulk-family-assigner">
    <div class="assigner-header">
      <h3>Bulk Assign Family to Products</h3>
      <p class="subtitle">
        Select a vehicle family, search for products, then assign or remove the
        family to all selected products at once.
      </p>
    </div>

    <div class="layout-two-column">
      <!-- Left: Family + Product Search -->
      <div class="column column-left">
        <!-- Step 1: Pick Family (dropdown) -->
        <div class="step-section">
          <div class="step-label">1 — Select a Vehicle Family</div>
          <div class="family-dropdown" v-click-outside="closeFamilyDropdown">
            <div
              class="family-dropdown-trigger default-input"
              :class="{ open: familyDropdownOpen, disabled: loadingFamilies }"
              @click="toggleFamilyDropdown"
            >
              <template v-if="selectedFamily">
                <img
                  v-if="selectedFamily.imageUrl"
                  :src="selectedFamily.imageUrl"
                  class="family-thumb-sm"
                  alt=""
                />
                <span class="trigger-text">
                  <strong>{{ selectedFamily.catalogCode }}</strong>
                  — {{ selectedFamily.family }}
                  <span class="text-muted">({{ selectedFamily.serie }})</span>
                </span>
              </template>
              <template v-else>
                <span class="trigger-placeholder">{{
                  loadingFamilies ? "Loading..." : "Select a family..."
                }}</span>
              </template>
              <span class="dropdown-arrow">&#9662;</span>
            </div>

            <div class="family-dropdown-panel" v-if="familyDropdownOpen">
              <input
                ref="familySearchInput"
                v-model="familySearch"
                class="default-input dropdown-search"
                placeholder="Filter families..."
                @click.stop
              />
              <div class="families-list">
                <div
                  v-for="family in filteredFamilies"
                  :key="family.catalogCode"
                  class="family-row"
                  :class="{
                    selected:
                      selectedFamily &&
                      selectedFamily.catalogCode === family.catalogCode
                  }"
                  @click="selectFamily(family)"
                >
                  <img
                    v-if="family.imageUrl"
                    :src="family.imageUrl"
                    class="family-thumb"
                    alt=""
                  />
                  <div v-else class="family-thumb family-thumb--placeholder" />
                  <div class="family-row-info">
                    <span class="family-code-tag">{{
                      family.catalogCode
                    }}</span>
                    <span class="family-name">{{ family.family }}</span>
                    <span class="family-serie">{{ family.serie }}</span>
                  </div>
                </div>
                <div v-if="filteredFamilies.length === 0" class="empty-msg">
                  No families found
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Step 2: Search Products -->
        <div class="step-section" :class="{ disabled: !selectedFamily }">
          <div class="step-label">2 — Search Products</div>
          <div class="search-row">
            <input
              v-model="productSearch"
              class="default-input full"
              placeholder="Search by NKR code, unificado, or title..."
              :disabled="!selectedFamily"
            />
            <span v-if="searchingProducts" class="search-spinner"
              >&#10227;</span
            >
          </div>

          <div class="products-panel">
            <div v-if="searchResults.length > 0" class="search-results">
              <div class="results-header">
                <span>{{ searchResults.length }} results</span>
                <label class="checkbox-label">
                  <input
                    type="checkbox"
                    :checked="allResultsSelected"
                    @change="toggleSelectAll"
                  />
                  Select all
                </label>
              </div>
              <div class="results-list">
                <div
                  v-for="product in searchResults"
                  :key="getProductUniqueKey(product)"
                  class="product-row"
                  :class="{ selected: isProductSelected(product) }"
                  @click="toggleProduct(product)"
                >
                  <input
                    type="checkbox"
                    :checked="isProductSelected(product)"
                    @click.stop="toggleProduct(product)"
                  />
                  <img
                    v-if="product.image"
                    :src="product.image"
                    class="product-thumb"
                    alt=""
                  />
                  <div
                    v-else
                    class="product-thumb product-thumb--placeholder"
                  />
                  <div class="product-info">
                    <span class="product-code">{{ product.nkrCode }}</span>
                    <span class="product-desc">{{ product.customTitle }}</span>
                  </div>
                </div>
              </div>
            </div>
            <div
              v-else-if="searched && searchResults.length === 0"
              class="empty-msg"
            >
              No products found
            </div>
            <div v-else-if="!searched" class="empty-msg">
              Type to search products
            </div>
          </div>
        </div>
      </div>

      <!-- Right: Selection Summary + Assign -->
      <div class="column column-right">
        <div class="summary-section">
          <div class="step-label">3 — Review &amp; Assign</div>

          <div class="summary-family" v-if="selectedFamily">
            <div class="summary-block">
              <span class="summary-block-label">Family</span>
              <span class="family-code-tag">{{
                selectedFamily.catalogCode
              }}</span>
              {{ selectedFamily.family }}
            </div>
          </div>
          <div v-else class="empty-msg">No family selected</div>

          <div class="summary-products" v-if="selectedProducts.length > 0">
            <div class="summary-block-label">
              Products selected ({{ selectedProducts.length }})
            </div>
            <div class="selected-products-list">
              <div
                v-for="product in selectedProducts"
                :key="getProductUniqueKey(product)"
                class="selected-product-row"
              >
                <img
                  v-if="product.image"
                  :src="product.image"
                  class="product-thumb-sm"
                  alt=""
                />
                <div
                  v-else
                  class="product-thumb-sm product-thumb--placeholder"
                />
                <span class="product-code">{{ product.nkrCode }}</span>
                <span class="product-desc truncate">{{
                  product.customTitle
                }}</span>
                <span
                  v-if="assignResults[product.unificado]"
                  class="assign-badge"
                  :class="assignResults[product.unificado].status"
                >
                  {{ assignResults[product.unificado].label }}
                </span>
                <button
                  v-else
                  class="remove-btn"
                  @click="removeProduct(product)"
                >
                  &#10005;
                </button>
              </div>
            </div>
          </div>
          <div v-else class="empty-msg">No products selected</div>

          <div
            class="assign-actions"
            v-if="selectedFamily && selectedProducts.length > 0 && !assignDone"
          >
            <button
              class="btn-success-button full"
              :disabled="assigning"
              @click="runBulkAction('add')"
            >
              {{
                assigning && actionMode === "add"
                  ? `Assigning... (${assignProgress}/${selectedProducts.length})`
                  : `Assign "${selectedFamily.family}" to ${selectedProducts.length} product(s)`
              }}
            </button>
            <button
              class="btn-danger-button full"
              :disabled="assigning"
              @click="runBulkAction('remove')"
            >
              {{
                assigning && actionMode === "remove"
                  ? `Removing... (${assignProgress}/${selectedProducts.length})`
                  : `Remove "${selectedFamily.family}" from ${selectedProducts.length} product(s)`
              }}
            </button>
          </div>

          <div class="assign-summary" v-if="assignDone">
            <div class="summary-ok">
              &#10003; {{ assignResultCount.ok }} succeeded
            </div>
            <div class="summary-err" v-if="assignResultCount.err > 0">
              &#10007; {{ assignResultCount.err }} failed
            </div>
            <button class="btn-dark-button mt-1" @click="resetAll">
              Start Over
            </button>
            <button class="btn-outline-button mt-1" @click="clearResults">
              Clear
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { getVehicleFamilies } from "@/api/store/VehiclesService";
import {
  addFamilyApplications,
  removeFamilyApplications
} from "@/api/store/ApplicationsService";
import { simpleSearchProducts } from "@/api/store/ProductsService";

export default {
  name: "BulkFamilyAssigner",
  directives: {
    "click-outside": {
      bind(el, binding) {
        el._clickOutside = e => {
          if (!el.contains(e.target)) binding.value();
        };
        document.addEventListener("click", el._clickOutside);
      },
      unbind(el) {
        document.removeEventListener("click", el._clickOutside);
      }
    }
  },
  data() {
    return {
      // families
      families: [],
      loadingFamilies: false,
      familySearch: "",
      familyDropdownOpen: false,
      selectedFamily: null,
      // product search
      productSearch: "",
      searchingProducts: false,
      searched: false,
      searchResults: [],
      selectedProducts: [],
      // assign / remove
      actionMode: "add",
      assigning: false,
      assignProgress: 0,
      assignResults: {},
      assignDone: false
    };
  },
  computed: {
    filteredFamilies() {
      const seen = new Set();
      const unique = this.families.filter(f => {
        if (seen.has(f.catalogCode)) return false;
        seen.add(f.catalogCode);
        return true;
      });
      if (!this.familySearch) return unique;
      const q = this.familySearch.toLowerCase();
      return unique.filter(
        f =>
          (f.family && f.family.toLowerCase().includes(q)) ||
          (f.catalogCode && f.catalogCode.toLowerCase().includes(q)) ||
          (f.serie && f.serie.toLowerCase().includes(q))
      );
    },
    allResultsSelected() {
      return (
        this.searchResults.length > 0 &&
        this.searchResults.every(p => this.isProductSelected(p))
      );
    },
    assignResultCount() {
      const values = Object.values(this.assignResults);
      return {
        ok: values.filter(r => r.status === "ok").length,
        err: values.filter(r => r.status === "err").length
      };
    }
  },
  mounted() {
    this.loadFamilies();
  },
  watch: {
    productSearch(val) {
      clearTimeout(this._searchDebounce);
      if (!val || val.length < 2 || !this.selectedFamily) {
        if (!val) {
          this.searchResults = [];
          this.searched = false;
        }
        return;
      }
      this._searchDebounce = setTimeout(() => {
        this.searchProducts();
      }, 200);
    }
  },
  beforeDestroy() {
    if (this._cancelSource) this._cancelSource.cancel();
    clearTimeout(this._searchDebounce);
  },
  methods: {
    // -- Family dropdown --
    toggleFamilyDropdown() {
      if (this.loadingFamilies) return;
      this.familyDropdownOpen = !this.familyDropdownOpen;
      if (this.familyDropdownOpen) {
        this.$nextTick(() => {
          if (this.$refs.familySearchInput) {
            this.$refs.familySearchInput.focus();
          }
        });
      }
    },
    closeFamilyDropdown() {
      this.familyDropdownOpen = false;
    },

    // -- Families --
    async loadFamilies() {
      this.loadingFamilies = true;
      try {
        this.families = await getVehicleFamilies();
      } catch {
        this.$toast.error("Error loading families");
      } finally {
        this.loadingFamilies = false;
      }
    },
    selectFamily(family) {
      this.selectedFamily = family;
      this.familyDropdownOpen = false;
      this.familySearch = "";
      this.searchResults = [];
      this.selectedProducts = [];
      this.searched = false;
      this.assignResults = {};
      this.assignDone = false;
    },

    // -- Product search --
    getProductUniqueKey(product) {
      return product?.unificado || product?.nkrCode || product?.oe || "";
    },
    dedupeProducts(products = []) {
      const seen = new Set();
      return products.filter(product => {
        const key = this.getProductUniqueKey(product);
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    },
    async searchProducts() {
      if (!this.productSearch || this.productSearch.length < 2) return;

      // Cancel previous in-flight request
      if (this._cancelSource) this._cancelSource.cancel();
      this._cancelSource = axios.CancelToken.source();

      this.searchingProducts = true;
      this.searched = false;
      try {
        const res = await simpleSearchProducts(
          this.productSearch,
          50,
          this._cancelSource.token
        );
        this.searchResults = this.dedupeProducts(res.products);
      } catch (e) {
        if (!axios.isCancel(e)) {
          this.$toast.error("Error searching products");
          this.searchResults = [];
        }
      } finally {
        this.searchingProducts = false;
        this.searched = true;
      }
    },

    // -- Selection --
    isProductSelected(product) {
      const key = this.getProductUniqueKey(product);
      return this.selectedProducts.some(
        p => this.getProductUniqueKey(p) === key
      );
    },
    toggleProduct(product) {
      const key = this.getProductUniqueKey(product);
      if (this.isProductSelected(product)) {
        this.selectedProducts = this.selectedProducts.filter(
          p => this.getProductUniqueKey(p) !== key
        );
      } else {
        this.selectedProducts.push(product);
      }
    },
    removeProduct(product) {
      const key = this.getProductUniqueKey(product);
      this.selectedProducts = this.selectedProducts.filter(
        p => this.getProductUniqueKey(p) !== key
      );
    },
    toggleSelectAll() {
      if (this.allResultsSelected) {
        const keys = new Set(
          this.searchResults.map(product => this.getProductUniqueKey(product))
        );
        this.selectedProducts = this.selectedProducts.filter(
          p => !keys.has(this.getProductUniqueKey(p))
        );
      } else {
        const existing = new Set(
          this.selectedProducts.map(product =>
            this.getProductUniqueKey(product)
          )
        );
        this.searchResults.forEach(p => {
          const key = this.getProductUniqueKey(p);
          if (!existing.has(key)) this.selectedProducts.push(p);
        });
      }
    },

    // -- Bulk action --
    async runBulkAction(mode) {
      this.actionMode = mode;
      this.assigning = true;
      this.assignProgress = 0;
      this.assignResults = {};

      const fn =
        mode === "add" ? addFamilyApplications : removeFamilyApplications;

      for (const product of this.selectedProducts) {
        try {
          await fn(product.unificado, this.selectedFamily.catalogCode);
          this.$set(this.assignResults, product.unificado, {
            status: "ok",
            label: mode === "add" ? "✓ added" : "✓ removed"
          });
        } catch {
          this.$set(this.assignResults, product.unificado, {
            status: "err",
            label: "✗ error"
          });
        }
        this.assignProgress++;
      }

      this.assigning = false;
      this.assignDone = true;
    },
    resetAll() {
      this.selectedFamily = null;
      this.productSearch = "";
      this.searchResults = [];
      this.selectedProducts = [];
      this.searched = false;
      this.assignResults = {};
      this.assignDone = false;
      this.assignProgress = 0;
      this.actionMode = "add";
    },
    clearResults() {
      this.selectedProducts = [];
      this.assignResults = {};
      this.assignDone = false;
      this.assignProgress = 0;
      this.actionMode = "add";
    }
  }
};
</script>

<style lang="scss" scoped>
.default-input {
  width: 100%;
  border: 1px solid #9ca3af;
  border-radius: 6px;
  padding: 8px 12px;
  font-size: 0.9375rem;
  color: #111827;
  background: #fff;
  transition: border-color 0.15s;

  &::placeholder {
    color: #6b7280;
  }
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.15);
  }
  &:disabled {
    background: #f3f4f6;
    color: #9ca3af;
    cursor: not-allowed;
  }
}

.bulk-family-assigner {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  padding: 1.5rem;
}

.assigner-header {
  margin-bottom: 1.25rem;
  h3 {
    font-size: 1.25rem;
    font-weight: 600;
    margin-bottom: 0.25rem;
  }
  .subtitle {
    font-size: 0.875rem;
    color: #6b7280;
  }
}

.layout-two-column {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  align-items: flex-start;
}

.column {
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.step-section {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  &.disabled {
    opacity: 0.45;
    pointer-events: none;
  }
}

.step-label {
  font-weight: 700;
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #6b7280;
  margin-bottom: 0.25rem;
}

/* --- Family Dropdown --- */
.family-dropdown {
  position: relative;
}

.family-dropdown-trigger {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  user-select: none;

  &.disabled {
    pointer-events: none;
    opacity: 0.5;
  }
  &.open {
    border-color: #3b82f6;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.15);
  }
}

.trigger-text {
  flex: 1;
  font-size: 0.9rem;
  color: #111827;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.trigger-placeholder {
  flex: 1;
  color: #9ca3af;
  font-size: 0.9rem;
}

.dropdown-arrow {
  font-size: 0.75rem;
  color: #6b7280;
  flex-shrink: 0;
}

.text-muted {
  color: #9ca3af;
  font-weight: 400;
}

.family-dropdown-panel {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
  z-index: 50;
  display: flex;
  flex-direction: column;
}

.dropdown-search {
  border: none;
  border-bottom: 1px solid #e5e7eb;
  border-radius: 0.375rem 0.375rem 0 0;
  font-size: 0.875rem;
}

.families-list {
  max-height: 280px;
  overflow-y: auto;
}

.family-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  cursor: pointer;
  font-size: 0.9rem;
  border-bottom: 1px solid #f3f4f6;
  transition: background 0.1s;

  &:last-child {
    border-bottom: none;
  }
  &:hover {
    background: #f0f9ff;
  }
  &.selected {
    background: #dbeafe;
  }
}

.family-thumb {
  width: 32px;
  height: 32px;
  object-fit: cover;
  border-radius: 0.25rem;
  flex-shrink: 0;
  border: 1px solid #e5e7eb;

  &--placeholder {
    background: #f3f4f6;
  }
}

.family-thumb-sm {
  width: 24px;
  height: 24px;
  object-fit: cover;
  border-radius: 0.2rem;
  flex-shrink: 0;
}

.family-row-info {
  display: flex;
  flex-direction: column;
  gap: 0.05rem;
  min-width: 0;

  .family-name {
    font-weight: 600;
    color: #111827;
    font-size: 0.875rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .family-serie {
    font-size: 0.75rem;
    color: #9ca3af;
  }
}

.family-code-tag {
  font-family: monospace;
  font-size: 0.8rem;
  font-weight: 700;
  background: #f3f4f6;
  padding: 0.1rem 0.35rem;
  border-radius: 0.2rem;
  white-space: nowrap;
  color: #374151;
}

/* --- Search --- */
.search-row {
  display: flex;
  gap: 0.5rem;
  align-items: center;
  position: relative;

  input {
    flex: 1;
  }
}

.search-spinner {
  position: absolute;
  right: 12px;
  font-size: 1rem;
  color: #3b82f6;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* --- Products panel --- */
.products-panel {
  min-height: 700px;
  display: flex;
  flex-direction: column;
}

.search-results {
  border: 1px solid #e5e7eb;
  border-radius: 0.375rem;
  overflow: hidden;
  flex: 1;
  display: flex;
  flex-direction: column;
}

.results-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0.75rem;
  background: #f9fafb;
  font-size: 0.875rem;
  color: #374151;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;
}

.results-list {
  flex: 1;
  overflow-y: auto;
}

.product-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  cursor: pointer;
  font-size: 0.9375rem;
  border-bottom: 1px solid #f3f4f6;
  transition: background 0.1s;

  &:last-child {
    border-bottom: none;
  }
  &:hover {
    background: #f9fafb;
  }
  &.selected {
    background: #dbeafe;
  }
}

.product-thumb {
  width: 36px;
  height: 36px;
  object-fit: cover;
  border-radius: 0.25rem;
  flex-shrink: 0;
  border: 1px solid #e5e7eb;

  &--placeholder {
    background: #f3f4f6;
  }
}

.product-thumb-sm {
  width: 24px;
  height: 24px;
  object-fit: cover;
  border-radius: 0.2rem;
  flex-shrink: 0;
  border: 1px solid #e5e7eb;

  &--placeholder {
    background: #f3f4f6;
  }
}

.product-info {
  display: flex;
  flex-direction: column;
  gap: 0.05rem;
  min-width: 0;
  flex: 1;
}

.product-code {
  font-family: monospace;
  font-size: 0.9rem;
  font-weight: 700;
  color: #111827;
  white-space: nowrap;
}

.product-desc {
  color: #1f2937;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 0.85rem;
  font-weight: 500;
}

/* --- Summary --- */
.summary-section {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.summary-family {
  font-size: 0.9375rem;
  font-weight: 600;
  color: #374151;
}

.summary-block {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 0.375rem;
}

.summary-block-label {
  font-size: 0.8rem;
  font-weight: 700;
  text-transform: uppercase;
  color: #6b7280;
  letter-spacing: 0.05em;
  margin-bottom: 0.25rem;
}

.selected-products-list {
  max-height: 500px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  margin-top: 0.5rem;
}

.selected-product-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.4rem 0.6rem;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 0.25rem;
  font-size: 0.9rem;
}

.truncate {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.remove-btn {
  background: none;
  border: none;
  color: #9ca3af;
  cursor: pointer;
  padding: 0 0.25rem;
  font-size: 0.75rem;
  line-height: 1;
  &:hover {
    color: #ef4444;
  }
}

.assign-badge {
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.15rem 0.4rem;
  border-radius: 9999px;
  white-space: nowrap;
  flex-shrink: 0;

  &.ok {
    background: #d1fae5;
    color: #065f46;
  }
  &.err {
    background: #fee2e2;
    color: #991b1b;
  }
}

.assign-actions {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  .full {
    width: 100%;
  }
}

.assign-summary {
  padding: 0.75rem;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 0.375rem;
  font-size: 0.9375rem;

  .summary-ok {
    color: #065f46;
    font-weight: 600;
  }
  .summary-err {
    color: #991b1b;
    font-weight: 600;
  }
  .mt-1 {
    margin-top: 0.5rem;
  }
}

.btn-outline-button {
  padding: 0.5rem 1rem;
  background: white;
  color: #374151;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-weight: 600;
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #f3f4f6;
    border-color: #9ca3af;
  }
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  cursor: pointer;
  font-size: 0.875rem;
}

.loading-msg,
.empty-msg {
  padding: 1rem;
  text-align: center;
  color: #9ca3af;
  font-size: 0.875rem;
}

.btn-success-button {
  padding: 0.625rem 1.25rem;
  background: #10b981;
  color: white;
  border: none;
  border-radius: 0.375rem;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: #059669;
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  &.full {
    width: 100%;
  }
}

.btn-danger-button {
  padding: 0.625rem 1.25rem;
  background: #ef4444;
  color: white;
  border: none;
  border-radius: 0.375rem;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: #dc2626;
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  &.full {
    width: 100%;
  }
}
</style>
